import { HttpClient } from '@angular/common/http';
import { Service, inject } from '@angular/core';
import { catchError, combineLatest, forkJoin, map, type Observable, of, switchMap, tap } from 'rxjs';

import { type Company, type CompanyCreate, type CompanySecondStepCreate, type CompanySecondStepPrefill, type CompanyUpdate } from '../models/company/company';
import { type TokenKey } from '../models/user/token-key';
import { filterNull } from '../rxjs/filter-null';
import { firstNonNullable } from '../rxjs/first-non-nullable';

import { AppConfigService } from './app-config.service';
import { CompanyDocumentsService } from './company-documents.service';
import { AppErrorMapper } from './mappers/app-error.mapper';
import { CompanySecondStepPrefillMapper } from './mappers/company/company-second-step-prefill.mapper';
import { CompanyMapper } from './mappers/company/company.mapper';
import { type CompanyCreateDto, type CompanyDto, type CompanySecondStepPrefillDto } from './mappers/dto/company/company.dto';
import { TokenMapper } from './mappers/token.mapper';
import { UserService } from './user.service';

/** Companies service. */
@Service()
export class CompaniesService {
	private readonly appErrorMapper = inject(AppErrorMapper);

	private readonly httpClient = inject(HttpClient);

	private readonly companyMapper = inject(CompanyMapper);

	private readonly companyDocumentsService = inject(CompanyDocumentsService);

	private readonly userService = inject(UserService);

	private readonly companySecondStepPrefillMapper = inject(CompanySecondStepPrefillMapper);

	private readonly tokenMapper = inject(TokenMapper);

	private readonly companiesUrl: URL;

	private readonly companyUrl: URL;

	private readonly registrationVerificationUrl: string;

	public constructor() {
		const appConfig = inject(AppConfigService);

		this.companiesUrl = new URL('companies/', appConfig.apiUrl);
		this.companyUrl = new URL('company/', this.companiesUrl);
		this.registrationVerificationUrl = new URL('companies/company/registration_url_verify/', appConfig.apiUrl).toString();
	}

	/** Get company information of current user. */
	public getCurrentCompany(): Observable<Company> {
		return this.userService.currentUser$.pipe(
			firstNonNullable(),
			map(user => user.companyId),
			filterNull(),
			switchMap(companyId => this.getCompanyById(companyId)),
		);
	}

	/**
	 * Get company information by ID.
	 * @param id Company ID.
	 */
	public getCompanyById(id: Company['id']): Observable<Company> {
		const url = new URL(`${id}/`, this.companyUrl);
		return this.httpClient.get<CompanyDto>(url.toString()).pipe(
			map(dto => this.companyMapper.fromDto(dto)),
			this.appErrorMapper.catchHttpErrorToAppError(),
		);
	}

	/**
	 * Register company with second registration step.
	 * @param data Company second step create data.
	 * @param key Key token for registration company.
	 */
	public register(data: CompanySecondStepCreate, key: TokenKey): Observable<void> {
		const { uid: companyId, token } = this.tokenMapper.toDto(key);
		const url = new URL(`${companyId}/registration_second_step/`, this.companyUrl);

		const businessOwnersWithPrepareDocs = data.businessOwners.map(
			businessOwner => this.companyDocumentsService.prepareDocuments(businessOwner.documents).pipe(
				map(documents => ({ ...businessOwner, documents })),
			),
		);

		const preparedVoidChecks$ = this.companyDocumentsService.prepareDocuments(data.voidCheck);
		return combineLatest([
			forkJoin(businessOwnersWithPrepareDocs),
			preparedVoidChecks$,
		]).pipe(
			switchMap(([businessOwners, voidCheck]) => this.httpClient.put<void>(
				url.toString(),
				this.companyMapper.toCompanySecondStepCreateDto({ ...data, businessOwners, voidCheck }, token),
			).pipe(
				this.appErrorMapper.catchHttpErrorToAppErrorWithValidationSupport(
					this.companyMapper.validationErrorFromCompanySecondStepCreateDto.bind(this.companyMapper),
				),
			)),
		);
	}

	/**
	 * Get data from the first step of registration for prefilling the form.
	 * @param token Token for company registration.
	 */
	public getFirstStepRegistrationCompanyData(token: TokenKey): Observable<CompanySecondStepPrefill> {
		const encodedId = this.tokenMapper.toDto(token).uid;
		const url = new URL(`${encodedId}/auto_complete/`, this.companyUrl);
		return this.httpClient.get<CompanySecondStepPrefillDto>(url.toString()).pipe(
			map(dto => ({
				...this.companySecondStepPrefillMapper.fromDto(dto),
				token,
			})),
		);
	}

	/**
	 * When user open link for second step registration we send request with token
	 * to check that link is active, user could't use one link twice.
	 * @param data Token key data.
	 */
	public verifyRegistrationToken(data: TokenKey): Observable<boolean> {
		return this.httpClient.post<void>(
			this.registrationVerificationUrl,
			this.tokenMapper.toDto(data),
		).pipe(
			map(() => true),
			catchError(() => of(false)),
		);
	}

	/**
	 * Create company with base fields to continue step-by-step registration.
	 * @param data Company creation data.
	 */
	public create(data: CompanyCreate): Observable<void> {
		return this.httpClient.post<CompanyCreateDto>(
			this.companyUrl.toString(),
			this.companyMapper.toCompanyCreateDto(data),
		).pipe(
			map(() => undefined),
			this.appErrorMapper.catchHttpErrorToAppErrorWithValidationSupport(
				this.companyMapper.validationErrorFromCompanyCreateDto.bind(this.companyMapper),
			),
		);
	}

	/**
	 * Update company.
	 * @param data Company update data.
	 * @param id Company ID.
	 */
	public update(data: CompanyUpdate, id: Company['id']): Observable<void> {
		const url = new URL(`${id}/`, this.companyUrl);

		const businessOwnersWithPrepareDocs = data.businessOwners.map(
			businessOwner => this.companyDocumentsService.prepareDocuments(businessOwner.documents).pipe(
				map(documents => ({ ...businessOwner, documents })),
			),
		);
		return forkJoin(businessOwnersWithPrepareDocs).pipe(
			switchMap(businessOwners => this.httpClient.put<void>(
				url.toString(),
				this.companyMapper.toCompanyUpdateDto({ ...data, businessOwners }),
			).pipe(
				this.appErrorMapper.catchHttpErrorToAppErrorWithValidationSupport(
					this.companyMapper.validationErrorFromCompanyUpdateDto.bind(this.companyMapper),
				),
			)),
		);
	}

	/**
	 * Update company and refresh current user
	 * because he related to business owner company data.
	 * @param data Company update data.
	 * @param id Company ID.
	 */
	public updateWithBusinessOwner(data: CompanyUpdate, id: Company['id']): Observable<void> {
		return this.update(data, id).pipe(
			tap(() => this.userService.refreshCurrentUser()),
		);
	}
}
